'use client';
export const dynamic = 'force-dynamic';
import { useState, useEffect, Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import { AppNav } from '@/components/AppNav';
import { PageTabs, useUrlTab } from '@/components/PageTabs';
import { DATA_SURFACES } from '@/lib/data-surfaces';
import { HoldingsList } from '@/components/research/HoldingsList';
import { StockStoryDrawer } from '@/components/StockStoryDrawer';
import { StockDetail } from '@/components/StockDetail';
import { useMemo } from 'react';
import { useResource } from '@/lib/use-resource';
import type { ScoredStock } from '@/lib/compute';
import { PortfolioHeadlineCard } from '@/components/research/PortfolioHeadlineCard';
import { SectorPie } from '@/components/research/SectorPie';
import { PortfolioAnalytics } from '@/components/research/PortfolioAnalytics';
import { SectorVsBenchmark } from '@/components/research/SectorVsBenchmark';
import { DrawdownChart } from '@/components/research/DrawdownChart';
import { CorrelationHeatmap } from '@/components/research/CorrelationHeatmap';
import { RebalanceSimulator } from '@/components/research/RebalanceSimulator';
import { EarningsImpactTable } from '@/components/research/EarningsImpactTable';
import { UpcomingEarnings } from '@/components/research/UpcomingEarnings';
import { AmBriefCard } from '@/components/research/AmBriefCard';
import { PortfolioScorecard } from '@/components/research/PortfolioScorecard';
import { HoldingsHistory } from '@/components/research/HoldingsHistory';
import { PortfolioVisualizer } from '@/components/research/PortfolioVisualizer';
import { ScoredNewsFeed } from '@/components/research/ScoredNewsFeed';
import { AlphaZoneAttribution } from '@/components/research/AlphaZoneAttribution';

function StockLinker({ onSymbol }: { onSymbol: (s: string) => void }) {
  const searchParams = useSearchParams();
  useEffect(() => {
    const sym = searchParams.get('stock');
    if (sym) onSymbol(sym.toUpperCase());
  }, [searchParams, onSymbol]);
  return null;
}

const PORTFOLIO_TABS = DATA_SURFACES.filter(s => s.href === '/portfolio' && s.kind === 'tab');

export default function PortfolioPage() {
  const [tab, setTab] = useUrlTab(PORTFOLIO_TABS, 'overview');
  const activeSurface = PORTFOLIO_TABS.find(s => s.id === tab) ?? PORTFOLIO_TABS[0];
  const [refreshing, setRefreshing] = useState(false);
  // Click-on-symbol → unified one-page view: StockDetail (left half) +
  // StockStoryDrawer (right half), shared backdrop, single close.
  const [selectedSymbol, setSelectedSymbol] = useState<string | null>(null);
  const stocksResp = useResource<{ stocks: ScoredStock[] }>('/api/stocks');
  const allStocks: ScoredStock[] = useMemo(() => stocksResp.data?.stocks ?? [], [stocksResp.data]);
  const selectedStock = useMemo(
    () => (selectedSymbol ? allStocks.find((s) => s.symbol === selectedSymbol) ?? null : null),
    [selectedSymbol, allStocks],
  );

  // ?stock=SYMBOL — opened when clicking a ticker in the Alerts drawer.
  // Must be in a child component wrapped in Suspense (Next.js requirement).

  const handleUpdatePortfolio = async () => {
    setRefreshing(true);
    try {
      const res = await fetch('/api/refresh-prices', { method: 'POST' });
      const j = await res.json() as { ok?: boolean };
      if (j.ok) {
        // Reload to repaint all surfaces against the new prices. The
        // DataAsOf badge picks up the bumped mtime and shows the new time.
        window.location.reload();
      }
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0e14] text-[#e2e8f0] pb-12">
      <AppNav />
      <div className="max-w-6xl mx-auto px-4 pt-2">
        <div className="sticky top-[36px] z-40 bg-[#0a0e14] -mx-4 px-4 pt-1 pb-2 border-b border-[#1e293b] shadow-[0_4px_8px_-4px_rgba(0,0,0,0.4)]">
        <header className="mb-2 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white">Portfolio</h1>
            <p className="text-[12px] text-[#94a3b8] mt-1">
              Everything about the active book — overview, scorecard, earnings, and risk.
            </p>
          </div>
          <div className="flex-shrink-0 flex items-center gap-2">
            <button
              type="button"
              onClick={handleUpdatePortfolio}
              disabled={refreshing}
              title="Pulls live FMP quotes for all 763 stocks and recomputes the portfolio snapshot. ~1s."
              className="text-[11px] px-3 py-1.5 rounded border border-emerald-400/50 text-emerald-300 hover:bg-emerald-400/10 hover:border-emerald-400/70 transition-colors uppercase tracking-wide font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {refreshing ? 'Updating…' : 'Update Portfolio'}
            </button>
            <a
              href="/api/portfolio-pdf"
              target="_blank"
              rel="noopener noreferrer"
              title="Generates a PDF of the current portfolio (live FMP prices, sector exposure pie)"
              className="text-[11px] px-3 py-1.5 rounded border border-amber-400/40 text-amber-300 hover:bg-amber-400/10 hover:border-amber-400/70 transition-colors uppercase tracking-wide font-semibold"
            >
              Holdings PDF
            </a>
            <a
              href="/api/portfolio-scorecard-pptx"
              target="_blank"
              rel="noopener noreferrer"
              title="Generates a one-slide PPTX of the deck-style portfolio scorecard (live FMP prices)"
              className="text-[11px] px-3 py-1.5 rounded border border-amber-400/40 text-amber-300 hover:bg-amber-400/10 hover:border-amber-400/70 transition-colors uppercase tracking-wide font-semibold"
            >
              Scorecard PPTX
            </a>
          </div>
        </header>

        <PageTabs surfaces={PORTFOLIO_TABS} active={tab} onChange={setTab} />

        {activeSurface && (
          <p className="text-[11px] text-[#64748b] mb-2 italic">{activeSurface.label}</p>
        )}
        </div>
        <div className="mb-4" />

        {tab === 'overview' && (
          <div className="space-y-6">
            {/* Section 1: Description — mirrors the prose paragraph on
                /alpha-zone so both pages open with the same shape. */}
            <p className="text-[13px] text-[#94a3b8] leading-relaxed max-w-4xl">
              The active book — 15 holdings plus cash. Names sourced from the visualizer
              model carry a separate tag in the attribution below (currently MU, VST,
              TLN); the rest were held before the model surfaced them. Weights are
              computed live from <span className="font-mono text-[#cbd5e1]">shares × today&rsquo;s price</span>,
              so book drift updates automatically as prices move. Sector mix and
              concentration are equity-only; cash shows separately. Same section
              ordering as the Alpha Zone page so the two read as siblings.
            </p>

            {/* Section 2: Headline performance card. */}
            <PortfolioHeadlineCard />

            {/* Section 3: 3D scatter scoped to portfolio symbols. */}
            <PortfolioVisualizer />

            {/* Section 4: The actual holdings table. */}
            <HoldingsList onSelectSymbol={setSelectedSymbol} />

            {/* Material News — Claude-scored sentiment + materiality + taxonomy.
                Steve directive 2026-05-23: replace keyword feed (eventually) with
                this scored output, default to HIGH + MEDIUM materiality. Calibration
                page at /news-ab compares side-by-side. */}
            <ScoredNewsFeed scope="held" title="Material News · Portfolio" />

            {/* Section 5: Visualizer-sourced vs pre-existing attribution.
                Same component as /alpha-zone, reads `visualizerSourced`
                from data/portfolio.json. */}
            <AlphaZoneAttribution />

            {/* Analytics + sector mix. Snapshot retired 2026-05-09:
                Avg-composite lives in HoldingsList totals, top sector lives
                in the Sector Mix donut, and holdings count is in the headline
                card — the snapshot strip was pure duplication. */}
            <PortfolioAnalytics mode="main" />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <SectorPie />
              <SectorVsBenchmark />
            </div>
          </div>
        )}

        {tab === 'scorecard' && <PortfolioScorecard />}

        {tab === 'history' && <HoldingsHistory />}

        {tab === 'earnings' && (
          <div className="space-y-6">
            <UpcomingEarnings />
            {/* EpsRevisionChart removed 2026-05-22 — per-holding chart now
                lives in the StockStoryDrawer; no need for a portfolio-wide
                grid duplicate here. */}
            <EarningsImpactTable />
            <div className="border border-[#1e293b] rounded-lg bg-[#0d1117]/50 p-4">
              <div className="text-[10px] uppercase tracking-wider text-[#94a3b8] mb-2 font-semibold">Morning Brief</div>
              <AmBriefCard />
            </div>
          </div>
        )}

        {tab === 'risk' && (
          <div className="space-y-6">
            <DrawdownChart />
            <CorrelationHeatmap />
            <PortfolioAnalytics mode="risk" />
            <RebalanceSimulator />
          </div>
        )}
      </div>
      <Suspense fallback={null}>
        <StockLinker onSymbol={setSelectedSymbol} />
      </Suspense>
      {selectedSymbol && (
        <>
          {/* Shared backdrop — closing either pane closes both. */}
          <div className="fixed inset-0 bg-black/70 z-40" onClick={() => setSelectedSymbol(null)} />
          {selectedStock && (
            <StockDetail
              stock={selectedStock}
              allStocks={allStocks}
              pinnedSymbols={new Set()}
              onTogglePin={() => {}}
              onClose={() => setSelectedSymbol(null)}
              noBackdrop
            />
          )}
          <StockStoryDrawer symbol={selectedSymbol} onClose={() => setSelectedSymbol(null)} />
        </>
      )}
    </div>
  );
}
